// REPLACE your existing LoginPage.js handleSubmit method with this fixed version:

import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { ArrowRight, Calendar, Lock, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState({ state: 'idle', message: '' });

  // Use loading state from useAuth instead of managing separately
  const { login, loading, user } = useAuth(); // Get loading from useAuth
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  // FIXED: Use useEffect to handle navigation when user state changes
  useEffect(() => {
    // If user becomes authenticated while on login page, navigate away
    if (user && !loading) {
      console.log('✅ User authenticated, navigating to dashboard');
      // Small delay to ensure auth state is fully settled
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
    }
  }, [user, loading, navigate, from]);

  // FIXED: Simplified handleSubmit without conflicting loading states
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    console.log('🔐 Starting login process...');
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        console.log('✅ Login successful, waiting for auth state to settle...');
        // Navigation will happen automatically via useEffect when user state updates
        // Add a fallback navigation in case useEffect doesn't trigger
        setTimeout(() => {
          if (!user) {
            console.log('🔄 Fallback navigation trigger');
            navigate(from, { replace: true });
          }
        }, 2000);
      } else {
        console.error('❌ Login failed:', result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetStatus({ state: 'loading', message: '' });
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, resetEmail);
      setResetStatus({ state: 'success', message: 'Check your inbox — reset link sent.' });
    } catch (err) {
      setResetStatus({ state: 'error', message: 'Could not send reset email. Check the address and try again.' });
    }
  };

  // Don't show login form if user is already authenticated
  if (user && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Calendar className="h-8 w-8 text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Already Logged In</h2>
          <p className="text-gray-600 mb-4">Redirecting to your dashboard...</p>
          <Link 
            to="/dashboard" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Continue to Dashboard →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
        <div className="flex justify-center mb-6">
          <Calendar className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="text-center text-3xl font-bold text-gray-900">
          Welcome back to Booker.Chat
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to your business dashboard
        </p>
        
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-600">Don't have an account? </span>
          <Link to="/register" className="text-blue-600 hover:text-blue-500 font-medium">
            Start your free trial
          </Link>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Display any messages from registration flow */}
          {location.state?.message && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="text-green-800 text-sm">
                {location.state.message}
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-800 text-sm">
                {error}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setShowReset(true); setResetStatus({ state: 'idle', message: '' }); }}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Forgot password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    Sign in to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {showReset && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Reset your password</h3>
              {resetStatus.state === 'success' ? (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">
                  {resetStatus.message}
                </p>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {resetStatus.state === 'error' && (
                    <p className="text-xs text-red-600">{resetStatus.message}</p>
                  )}
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={resetStatus.state === 'loading'}
                      className="flex-1 py-2 px-3 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {resetStatus.state === 'loading' ? 'Sending…' : 'Send reset link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReset(false)}
                      className="py-2 px-3 border border-gray-300 text-sm rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">First time here?</span>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <Link
                to="/register"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Start Your Free Trial
              </Link>
              
              <Link
                to="/demo"
                className="w-full flex justify-center py-2 px-4 text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                See How It Works
              </Link>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Access Your Dashboard To:
            </h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Manage your services and pricing</li>
              <li>• View AI booking analytics</li>
              <li>• Configure business settings</li>
              <li>• Monitor customer interactions</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <div className="flex justify-center space-x-4 text-sm text-gray-500">
          <Link to="/support" className="hover:text-gray-700">Support</Link>
          <span>•</span>
          <Link to="/about" className="hover:text-gray-700">About</Link>
          <span>•</span>
          <Link to="/legal" className="hover:text-gray-700">Privacy</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;